import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, MousePointerClick, Smartphone, Upload, Wand2 } from '@/components/icons';
import { api, errorMessage, type PerfImportResult } from '@/lib/api';
import { RECORDER_PERF_MIN_VERSION, detectRecorder, onRecorderScriptSaved, startRecorder, versionAtLeast } from '@/lib/recorderBridge';
import type { Project } from '@/lib/types';
import { startProblem } from '@/components/NewTestChooser';
import { ScriptReview } from '@/components/perf/ScriptReview';
import { Button, Card, CardBody, CardHeader, ErrorNotice, Field, Input, PageHeader, PageLoader, TabPanel, Tabs, Toggle, useToast } from '@/components/ui';

type Source = 'browser' | 'har' | 'phone';

/** Read a HAR file chosen in the browser. */
async function readHar(file: File): Promise<unknown> {
  try {
    return JSON.parse(await file.text());
  } catch {
    throw new Error(`${file.name} is not a HAR file (it is not JSON).`);
  }
}

function HarPicker({ label, hint, file, onChange }: { label: string; hint: string; file: File | null; onChange: (f: File | null) => void }) {
  return (
    <Field label={label} hint={hint}>
      {(id) => (
        <label htmlFor={id} className="flex cursor-pointer items-center gap-3 rounded-field bg-surface-sunken px-4 py-3 text-sm ring-1 ring-border ring-inset hover:ring-primary">
          <Upload className="size-4 text-primary" aria-hidden />
          <span className="min-w-0 flex-1 truncate text-fg">{file ? `${file.name} (${Math.round(file.size / 1024)} KB)` : 'Choose a .har file'}</span>
          <input id={id} type="file" accept=".har,application/json" className="sr-only" onChange={(e) => onChange(e.target.files?.[0] ?? null)} />
        </label>
      )}
    </Field>
  );
}

/** Step 1 from HAR files: the browser's (or a proxy's) recording, uploaded and correlated on the server. */
function HarSource({ project, onResult }: { project: { id: string; targetUrl?: string | null }; onResult: (r: PerfImportResult, name: string) => void }) {
  const toast = useToast();
  const [har, setHar] = useState<File | null>(null);
  const [har2, setHar2] = useState<File | null>(null);
  const [origins, setOrigins] = useState('');
  const [keepStatic, setKeepStatic] = useState(false);
  const [backendOnly, setBackendOnly] = useState(true);
  const analyse = useMutation({
    mutationFn: async () => {
      const body = {
        har: await readHar(har!),
        ...(har2 ? { har2: await readHar(har2) } : {}),
        origins: origins.split(/[\s,]+/).map((o) => o.trim()).filter(Boolean),
        keepStatic,
        backendOnly,
      };
      return api.perfImport(project.id, body);
    },
    onSuccess: (r) => onResult(r, har?.name.replace(/\.har$/i, '').replace(/[_-]+/g, ' ').trim() || 'Performance test'),
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Card>
      <CardHeader title="1. Upload the recording" description="Chrome or Edge: open DevTools (F12) › Network, tick Preserve log, go through the journey, then right-click the list › Save all as HAR with content." />
      <CardBody className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <HarPicker label="Recording" hint="The journey, signed in with the test data of this project." file={har} onChange={setHar} />
          <HarPicker label="Same journey again (recommended)" hint="A second recording, in a new browser session: every rule gets confirmed." file={har2} onChange={setHar2} />
        </div>
        <Field label="Other hosts to keep (optional)" hint={`Requests to ${project.targetUrl ? new URL(project.targetUrl).host : 'the app’s address'} are kept; add e.g. your sign-in provider (https://login.example.com). Others are dropped.`}>
          {(id) => <Input id={id} value={origins} onChange={(e) => setOrigins(e.target.value)} placeholder="https://login.example.com" />}
        </Field>
        <div className="flex items-center gap-3">
          <Toggle checked={backendOnly} onChange={setBackendOnly} label="Backend API requests only" />
          <span className="text-sm text-fg-secondary" aria-hidden>
            Backend API requests only: leave out pages and front-end framework requests (turn off for apps that render pages on the server)
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Toggle checked={keepStatic} onChange={setKeepStatic} label="Keep images, fonts, styles and scripts" />
          <span className="text-sm text-fg-secondary" aria-hidden>
            Keep images, fonts, styles and scripts (usually served by a CDN, not your app)
          </span>
        </div>
        <div className="flex justify-end">
          <Button onClick={() => analyse.mutate()} disabled={!har} loading={analyse.isPending} icon={<Wand2 className="size-4" />}>
            Analyse
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

/** Where starting the recorder from this page got to. */
type RecordState =
  | { kind: 'idle' }
  | { kind: 'starting' }
  | { kind: 'started' }
  | { kind: 'install'; installed: string | null }
  | { kind: 'problem'; message: ReactNode };

/**
 * The 1QA Recorder's performance mode (0.6.0+), started from here in a new window at the project's app address. The
 * recorder saves the script straight to this project's Scripts and tells this page, which then opens it. `autoStart`:
 * the person pressed "New script", so the recorder starts at once.
 */
function RecordInBrowser({ project, autoStart, onAutoStarted }: { project: Project; autoStart: boolean; onAutoStarted: () => void }) {
  const navigate = useNavigate();
  const [state, setState] = useState<RecordState>({ kind: 'idle' });

  const start = useCallback(async () => {
    setState({ kind: 'starting' });
    const recorder = await detectRecorder();
    if (!recorder || !versionAtLeast(recorder.version, RECORDER_PERF_MIN_VERSION)) return setState({ kind: 'install', installed: recorder?.versionName ?? null });
    const r = await startRecorder({ orgId: project.orgId, projectId: project.id, url: project.targetUrl ?? null, perf: true });
    if (r.ok) setState({ kind: 'started' });
    else setState({ kind: 'problem', message: startProblem(r, 'Start recording') });
  }, [project.orgId, project.id, project.targetUrl]);

  // Once per press of "New script" (also under React's double effects in development).
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!autoStart || autoStarted.current) return;
    autoStarted.current = true;
    onAutoStarted();
    void start();
  }, [autoStart, onAutoStarted, start]);

  // The recorder saved the script to this project: show it.
  useEffect(
    () => onRecorderScriptSaved(({ projectId, scriptId }) => projectId === project.id && navigate(`/projects/${project.id}/perf/scripts/${scriptId}`)),
    [navigate, project.id],
  );

  const busy = state.kind === 'starting';
  return (
    <Card>
      <CardHeader
        title="1. Record the journey"
        description="With the 1QA Recorder (Chrome or Edge): you click through your app once in a new window and it records every request the app sends."
      />
      <CardBody className="space-y-4 text-sm">
        {state.kind === 'started' ? (
          <p role="status" className="rounded-field bg-success-subtle p-3 text-fg ring-1 ring-success/40 ring-inset">
            Recording in a new window{project.targetUrl ? ` at ${new URL(project.targetUrl).host}` : ''}.{' '}
            Go through the journey with this project’s test data, press <strong>Stop</strong> on the recording bar, name the script and save it to{' '}
            <strong>{project.name}</strong>. It is added to the Scripts and opens here.
          </p>
        ) : state.kind === 'install' ? (
          <p role="alert" className="rounded-field bg-warning-subtle p-3 text-fg ring-1 ring-warning/40 ring-inset">
            {state.installed
              ? `Your recorder (version ${state.installed}) cannot record performance scripts on its own yet - version ${RECORDER_PERF_MIN_VERSION} or newer is needed.`
              : 'The 1QA Recorder is not installed in this browser (or this page was opened before it was installed).'}{' '}
            Get it from{' '}
            <Link to="/helpers/recorder" className="text-link hover:underline">
              Helper services › Browser recorder
            </Link>
            , connect it to 1QA, then reload this page.
          </p>
        ) : state.kind === 'problem' ? (
          <p role="alert" className="rounded-field bg-warning-subtle p-3 text-fg ring-1 ring-warning/40 ring-inset">
            {state.message}
          </p>
        ) : (
          <p className="text-fg-secondary">
            The recorder opens your app{project.targetUrl ? ` (${new URL(project.targetUrl).host})` : ''} in a new window. Go through the journey as a user
            would; each click names a transaction of the script.
          </p>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-fg-muted">
            Or click the recorder icon in the toolbar, choose <strong>Performance script</strong> and start there.
          </p>
          <Button onClick={() => void start()} loading={busy} variant={state.kind === 'idle' || state.kind === 'install' || state.kind === 'problem' ? undefined : 'outline'} icon={<MousePointerClick className="size-4" />}>
            {state.kind === 'started' ? 'Start again' : 'Start recording'}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * New performance script, recorded in the browser (the 1QA Recorder's performance mode, the default) or uploaded as HAR
 * files from DevTools or a proxy. Either way 1QA keeps the app's requests, finds every value the server or the browser
 * makes per session and turns it into a rule (auto-correlation), and turns typed test data into {{KEY}} references; the
 * person reviews the result and saves it to the project's script library.
 * Or recorded on a phone: the phone recorder of an Android project in performance mode (?source=phone lists them).
 * URL: ?source=browser|har|phone, ?capture=<recording uploaded by the recorder that 1QA could not save as a script by itself>.
 * Navigation state `{ record: true }` ("New script" buttons) starts the recorder at once.
 */
export function PerfImportPage() {
  const { projectId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Pressed "New script": start the recorder once, then forget it (a reload must not open another window).
  const autoStart = (location.state as { record?: boolean } | null)?.record === true;
  const forgetAutoStart = useCallback(() => navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null }), [navigate, location.pathname, location.search]);
  const project = useQuery({ queryKey: ['project', projectId], queryFn: () => api.project(projectId) });
  const [harResult, setHarResult] = useState<{ review: PerfImportResult; name: string; n: number } | null>(null);
  const captureId = params.get('capture');
  const capture = useQuery({ queryKey: ['perf-capture', captureId], queryFn: () => api.perfCapture(captureId!), enabled: !!captureId });

  if (project.isPending) return <PageLoader />;
  if (project.isError) return <ErrorNotice error={project.error} onRetry={() => project.refetch()} />;
  const source: Source = params.get('source') === 'har' ? 'har' : params.get('source') === 'phone' ? 'phone' : 'browser';
  const setSource = (s: Source) =>
    setParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        if (s !== 'browser') n.set('source', s);
        else n.delete('source');
        return n;
      },
      { replace: true },
    );
  const ready = capture.data?.status === 'READY' && capture.data.review ? capture.data : null;

  return (
    <div className="space-y-6">
      <Link to={`/projects/${projectId}?tab=scripts`} className="inline-flex items-center gap-1.5 text-sm text-fg-secondary hover:text-fg">
        <ArrowLeft className="size-4" /> {project.data.name}
      </Link>
      <PageHeader
        eyebrow="Performance"
        title="New performance script"
        description="A script is one user’s journey through your app, with every value that changes per session handled. Performance tests run scripts for many users at once."
      />

      <Tabs<Source>
        label="How to record the journey"
        value={source}
        onChange={setSource}
        items={[
          { id: 'browser', label: 'Record in the browser' },
          { id: 'phone', label: 'Record on an emulator or phone' },
          { id: 'har', label: 'Upload a recording (HAR)' },
        ]}
      />

      {source === 'browser' ? (
        <TabPanel id="browser">
          <div className="space-y-6">
            {captureId ? (
              capture.isPending ? (
                <PageLoader />
              ) : capture.isError ? (
                <ErrorNotice error={capture.error} onRetry={() => capture.refetch()} />
              ) : !ready ? (
                <ErrorNotice error={new Error(capture.data.error ?? 'This recording has no script to review.')} />
              ) : (
                <ScriptReview
                  key={ready.id}
                  projectId={projectId}
                  review={ready.review!}
                  defaultName={ready.testName.slice(0, 200)}
                  source="recorder"
                  sourceTestId={ready.testCaseId || null}
                  secondHint="One recording: record the same journey once more and compare, or run the check before the load."
                />
              )
            ) : (
              <RecordInBrowser project={project.data} autoStart={autoStart} onAutoStarted={forgetAutoStart} />
            )}
          </div>
        </TabPanel>
      ) : source === 'phone' ? (
        <TabPanel id="phone">
          <RecordOnPhone project={project.data} />
        </TabPanel>
      ) : (
        <TabPanel id="har">
          <div className="space-y-6">
            <HarSource project={project.data} onResult={(review, name) => setHarResult((prev) => ({ review, name, n: (prev?.n ?? 0) + 1 }))} />
            {harResult && (
              <ScriptReview key={harResult.n} projectId={projectId} review={harResult.review} defaultName={harResult.name} source="har" secondHint="Add a second recording of the same journey to confirm these rules." />
            )}
          </div>
        </TabPanel>
      )}
    </div>
  );
}

/**
 * A mobile app's script: recorded with the phone recorder of the organization's Android project for that app, in
 * performance mode, and saved back into this project.
 */
function RecordOnPhone({ project }: { project: Project }) {
  const projects = useQuery({ queryKey: ['projects', project.orgId], queryFn: () => api.projects(project.orgId) });
  const android = (projects.data ?? []).filter((p) => p.platform === 'ANDROID' && p.appId);
  return (
    <Card>
      <CardHeader
        title="Record your Android app on an emulator or a phone"
        description="Use the app on one of 1QA’s emulators, right here in the browser (nothing to set up, Flutter apps too), or on a phone connected through the 1QA Device Agent (1.4.0 or newer). 1QA records every request the app sends, and your steps name the script’s transactions."
      />
      <CardBody className="space-y-4">
        {projects.isPending ? (
          <PageLoader />
        ) : projects.isError ? (
          <ErrorNotice error={projects.error} onRetry={() => projects.refetch()} />
        ) : android.length === 0 ? (
          <p className="rounded-field bg-warning-subtle p-3 text-sm text-fg ring-1 ring-warning/40 ring-inset">
            Recording on a phone starts from the Android project of your app, and this organization has none you can use. Create one under Test projects › Android with the app’s package name.
          </p>
        ) : (
          <>
            <p className="text-sm text-fg-secondary">Choose the app. The phone recorder opens with “Performance script” chosen and this project as the place to save it.</p>
            <ul className="space-y-2">
              {android.map((p) => (
                <li key={p.id}>
                  <Link
                    to={`/projects/${p.id}/tests/record?mode=perf&perf=${encodeURIComponent(project.id)}`}
                    className="flex items-center gap-3 rounded-field px-3 py-2 ring-1 ring-border ring-inset hover:bg-surface-sunken"
                  >
                    <Smartphone className="size-4 text-fg-muted" aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-fg">{p.name}</span>
                      <span className="block truncate text-xs text-fg-muted">{p.appId}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
        <p className="text-xs text-fg-muted">
          With this project’s server certificate (Settings › Server certificates), phones need nothing installed and any build of the app works. Without
          one, the phone must trust the 1QA certificate of the agent’s computer and the app must be a build that trusts user certificates.
        </p>
      </CardBody>
    </Card>
  );
}
